import { normalizePhoneWithPlusPrefix } from "./phone";

export interface CSVValidationError {
  row: number;
  column: string;
  message: string;
  value?: string;
}

export interface CSVValidationResult {
  isValid: boolean;
  errors: CSVValidationError[];
  normalizedData?: string[][];
}

export interface CSVValidationOptions {
  requiredColumns: string[];
  phoneNumberColumns?: string[];
}

/**
 * Validates a CSV file for required columns and phone number format
 * @param csvContent - The CSV content as a string
 * @param options - Validation options including required columns and phone number columns
 * @returns Validation result with errors and normalized data
 */
export const validateCSV = (
  csvContent: string,
  options: CSVValidationOptions,
): CSVValidationResult => {
  const errors: CSVValidationError[] = [];
  const lines = csvContent.trim().split("\n");

  if (lines.length < 2) {
    return {
      isValid: false,
      errors: [
        {
          row: 1,
          column: "file",
          message:
            "CSV file must contain at least a header row and one data row",
        },
      ],
    };
  }

  const headerRow = lines[0];
  const headers = parseCSVRow(headerRow);
  const normalizedData: string[][] = [headers];

  for (const requiredColumn of options.requiredColumns) {
    if (!headers.includes(requiredColumn)) {
      errors.push({
        row: 1,
        column: requiredColumn,
        message: `Required column "${requiredColumn}" is missing`,
      });
    }
  }

  if (errors.length > 0) {
    return { isValid: false, errors };
  }

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    const rowNumber = i + 1;
    const rowData = parseCSVRow(line);

    if (rowData.length !== headers.length) {
      errors.push({
        row: rowNumber,
        column: "row",
        message: `Row has ${rowData.length} columns but header has ${headers.length} columns`,
        value: line,
      });
      continue;
    }

    const normalizedRow: string[] = [];

    for (let j = 0; j < headers.length; j++) {
      const header = headers[j];
      const value = rowData[j]?.trim() || "";
      const isRequired = options.requiredColumns.includes(header);
      const isPhoneColumn = options.phoneNumberColumns?.includes(header);

      if (isRequired && !value) {
        errors.push({
          row: rowNumber,
          column: header,
          message: `"${header}" is required`,
          value: value,
        });
      }

      if (isPhoneColumn && value) {
        const phoneValidation = validatePhoneNumber(value);
        if (!phoneValidation.isValid) {
          errors.push({
            row: rowNumber,
            column: header,
            message: phoneValidation.message,
            value: value,
          });
        } else {
          normalizedRow[j] = normalizePhoneWithPlusPrefix(value);
        }
      } else {
        normalizedRow[j] = value;
      }
    }

    normalizedData.push(normalizedRow);
  }

  return {
    isValid: errors.length === 0,
    errors,
    normalizedData: errors.length === 0 ? normalizedData : undefined,
  };
};

/**
 * Validates a phone number format
 * @param phoneNumber - The phone number to validate
 * @returns Validation result
 */
export const validatePhoneNumber = (
  phoneNumber: string,
): { isValid: boolean; message: string } => {
  const cleaned = phoneNumber.replace(/\D/g, "");

  if (cleaned.length === 10) {
    return { isValid: true, message: "" };
  }

  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return { isValid: true, message: "" };
  }

  if (phoneNumber.startsWith("+") && cleaned.length >= 10) {
    return { isValid: true, message: "" };
  }

  return {
    isValid: false,
    message:
      "Phone number must be in format (XXX) XXX-XXXX, 10 digits, or international format",
  };
};

/**
 * Parses a CSV row, handling quoted values and commas
 * @param row - The CSV row string
 * @returns Array of column values
 */
const parseCSVRow = (row: string): string[] => {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < row.length; i++) {
    const char = row[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
};

/**
 * Converts normalized data back to CSV format
 * @param data - The normalized data array
 * @returns CSV string
 */
export const convertToCSV = (data: string[][]): string =>
  data
    .map((row) =>
      row
        .map((cell) => {
          const escaped = cell.replace(/"/g, '""');
          return cell.includes(",") || cell.includes('"')
            ? `"${escaped}"`
            : escaped;
        })
        .join(","),
    )
    .join("\n");
